const FILTER_ID_FIELDS = ["subContractorIds"] as const;

export type StoredFilterConfig = {
	key: string | null;
	value: string | null;
};

/**
 * Removes a deleted subcontractor id from saved table filters.
 * Only `subContractorIds` string arrays change.
 */
export function removeDeletedSubContractorFilters(
	configs: StoredFilterConfig[],
	subContractorId: string,
): { key: string; value: string }[] {
	const changes: { key: string; value: string }[] = [];

	for (const config of configs) {
		if (!config.key?.endsWith(".filters") || config.value == null) continue;

		let parsed: unknown;
		try {
			parsed = JSON.parse(config.value);
		} catch {
			continue;
		}
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) continue;

		const record = parsed as Record<string, unknown>;
		const next: Record<string, unknown> = { ...record };
		let changed = false;

		for (const field of FILTER_ID_FIELDS) {
			const current = next[field];
			if (!isStringArray(current) || !current.includes(subContractorId)) {
				continue;
			}
			next[field] = current.filter((id) => id !== subContractorId);
			changed = true;
		}

		if (changed) {
			changes.push({ key: config.key, value: JSON.stringify(next) });
		}
	}

	return changes;
}

export type FilterCleanupResult = {
	filtersCleared: boolean;
	filterCleanupError?: unknown;
	nextConfigs: StoredFilterConfig[];
	updated: boolean;
};

/**
 * Persists removal of a deleted subcontractor from saved filters.
 * Writes run one key at a time and stop on the first failure. `nextConfigs`
 * includes only writes that succeeded, so the open session matches the server.
 */
export async function clearSavedSubContractorFilters({
	userId,
	configs,
	subContractorId,
	setValue,
}: {
	userId: string | undefined;
	configs: StoredFilterConfig[];
	subContractorId: string;
	setValue: (change: { key: string; value: string }) => Promise<void>;
}): Promise<FilterCleanupResult> {
	const changes = removeDeletedSubContractorFilters(configs, subContractorId);

	if (!userId) {
		return { filtersCleared: false, nextConfigs: configs, updated: false };
	}

	if (changes.length === 0) {
		return { filtersCleared: true, nextConfigs: configs, updated: false };
	}

	const savedKeys = new Set<string>();
	let writeError: unknown;
	for (const change of changes) {
		try {
			await setValue(change);
			savedKeys.add(change.key);
		} catch (error) {
			writeError = error;
			break;
		}
	}

	const nextConfigs = configs.map((config) => {
		if (!config.key || !savedKeys.has(config.key)) return config;
		const change = changes.find((item) => item.key === config.key);
		return change ? { ...config, value: change.value } : config;
	});

	if (writeError !== undefined) {
		return {
			filtersCleared: false,
			filterCleanupError: writeError,
			nextConfigs,
			updated: savedKeys.size > 0,
		};
	}

	return { filtersCleared: true, nextConfigs, updated: true };
}

function isStringArray(value: unknown): value is string[] {
	return (
		Array.isArray(value) && value.every((item) => typeof item === "string")
	);
}
